import type { BrandSlots } from "@cfg/brand-sdk";
import { useEffect, useLayoutEffect, useRef, type MouseEvent, type RefObject } from "react";
import { LogoDomain, LogoLockup, LogoMark } from "./logo.tsx";

/* -------------------------------------------------------------------------- */
/* Bench effects: one passive, rAF-throttled pointer listener for the whole    */
/* page. It moves the blueprint-grid lens (.bb-bench-lens, a 440 px window     */
/* whose inner grid is counter-translated so the dots stay put) and the        */
/* catalog-card spotlight (.bb-card-spot / .bb-card-ring-spot) with            */
/* `transform` only: no custom properties on :root, so a pointer move restyles */
/* four elements instead of the whole document. Card rects are cached per      */
/* card and dropped on scroll/resize. Fine pointers with motion allowed only;  */
/* touch and reduced motion keep the static top band from CSS. Also publishes  */
/* the header height as --bb-header-h (on resize only) for sticky panels.      */
/* -------------------------------------------------------------------------- */

const LENS = 220; // lens radius, px (matches .bb-bench-lens in css.ts)

function useBenchEffects(bench: RefObject<HTMLDivElement | null>, header: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = document.documentElement;
    const el = header.current;
    let ro: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== "undefined") {
      let last = 0;
      ro = new ResizeObserver(() => {
        const h = Math.round(el.getBoundingClientRect().height);
        if (h !== last) root.style.setProperty("--bb-header-h", `${(last = h)}px`);
      });
      ro.observe(el);
    }

    const benchEl = bench.current;
    const lens = benchEl?.querySelector<HTMLElement>(".bb-bench-lens");
    const grid = lens?.firstElementChild as HTMLElement | null | undefined;
    if (!benchEl || !lens || !grid) return () => ro?.disconnect();

    const fine = window.matchMedia("(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)");
    let frame = 0;
    let x = 0;
    let y = 0;
    let live = false;
    let stale = false; // scrolled since the last paint: re-hit-test the card under the pointer
    let card: HTMLElement | null = null;
    let rect: DOMRect | null = null;
    let spot: HTMLElement | null = null;
    let ring: HTMLElement | null = null;

    const setCard = (next: HTMLElement | null) => {
      if (next === card) return;
      card = next;
      rect = null;
      spot = next?.querySelector<HTMLElement>(".bb-card-spot") ?? null;
      ring = next?.querySelector<HTMLElement>(".bb-card-ring-spot") ?? null;
    };
    const paint = () => {
      frame = 0;
      lens.style.transform = `translate3d(${x - LENS}px, ${y - LENS}px, 0)`;
      grid.style.transform = `translate3d(${LENS - x}px, ${LENS - y}px, 0)`;
      if (stale) {
        stale = false;
        const hit = document.elementFromPoint(x, y);
        setCard(hit ? (hit.closest(".bb-card") as HTMLElement | null) : null);
      }
      if (!card) return;
      if (!rect) rect = card.getBoundingClientRect();
      const t = `translate3d(${x - rect.left - rect.width / 2}px, ${y - rect.top}px, 0)`;
      if (spot) spot.style.transform = t;
      if (ring) ring.style.transform = t;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const show = (on: boolean) => {
      if (on === live) return;
      live = on;
      benchEl.classList.toggle("is-live", on);
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" && e.pointerType !== "pen") return;
      x = e.clientX;
      y = e.clientY;
      const t = e.target;
      setCard(t instanceof Element ? (t.closest(".bb-card") as HTMLElement | null) : null);
      show(true);
      schedule();
    };
    const onOut = (e: PointerEvent) => {
      if (!e.relatedTarget) show(false); // left the window
    };
    const onHide = () => show(false);
    const onVisibility = () => {
      if (document.visibilityState !== "visible") show(false);
    };
    const onScroll = () => {
      rect = null;
      if (!live) return;
      stale = true;
      schedule();
    };
    const onResize = () => {
      rect = null;
    };
    const unbind = () => {
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerout", onOut);
      window.removeEventListener("blur", onHide);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
    const bind = () => {
      unbind();
      if (fine.matches) {
        window.addEventListener("pointermove", onMove, { passive: true });
        document.addEventListener("pointerout", onOut, { passive: true });
        window.addEventListener("blur", onHide);
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("scroll", onScroll, { passive: true, capture: true });
        window.addEventListener("resize", onResize, { passive: true });
      } else {
        show(false);
      }
    };
    bind();
    fine.addEventListener?.("change", bind);
    return () => {
      ro?.disconnect();
      fine.removeEventListener?.("change", bind);
      unbind();
      if (frame) cancelAnimationFrame(frame);
      show(false);
    };
  }, [bench, header]);
}

/**
 * Nav: the ink (raised pill + orange tick) rests on the active page only; a
 * separate quiet hover pill follows hover and keyboard focus and fades out on
 * leave.
 */
function useNavInk(nav: RefObject<HTMLElement | null>, activeHref: string | undefined) {
  useLayoutEffect(() => {
    const el = nav.current;
    if (!el) return;
    const hover = el.querySelector<HTMLElement>(".bb-nav-hover");
    const active = () => el.querySelector<HTMLElement>(".bb-nav-link.is-active");
    const placeInk = () => {
      const link = active();
      if (!link) return el.style.setProperty("--bb-ink-o", "0");
      el.style.setProperty("--bb-ink-x", `${link.offsetLeft}px`);
      el.style.setProperty("--bb-ink-w", `${link.offsetWidth}px`);
      el.style.setProperty("--bb-ink-o", "1");
    };
    placeInk();
    // Bring the current section into view when the nav scrolls on narrow screens.
    const a = active();
    if (a && el.scrollWidth > el.clientWidth) el.scrollLeft = Math.max(0, a.offsetLeft - 16);
    const ready = requestAnimationFrame(() => el.classList.add("is-ready"));

    let shown = false;
    const placeHover = (link: HTMLElement | null) => {
      if (!hover) return;
      if (!link || link.classList.contains("is-active")) {
        hover.classList.remove("is-on");
        return;
      }
      // First appearance jumps into place (no slide in from the last spot); later moves glide.
      hover.classList.toggle("is-snap", !shown);
      hover.style.transform = `translateX(${link.offsetLeft}px)`;
      hover.style.width = `${link.offsetWidth}px`;
      hover.classList.add("is-on");
      shown = true;
    };
    const over = (e: Event) => {
      const link = (e.target as Element).closest?.(".bb-nav-link") as HTMLElement | null;
      if (link) placeHover(link);
    };
    const leave = () => {
      shown = false;
      placeHover(null);
    };
    const onFocusOut = (e: FocusEvent) => {
      if (!el.contains(e.relatedTarget as Node | null)) leave();
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(placeInk) : undefined;
    ro?.observe(el);
    el.addEventListener("pointerover", over, { passive: true });
    el.addEventListener("pointerleave", leave, { passive: true });
    el.addEventListener("focusin", over);
    el.addEventListener("focusout", onFocusOut);
    return () => {
      cancelAnimationFrame(ready);
      ro?.disconnect();
      el.removeEventListener("pointerover", over);
      el.removeEventListener("pointerleave", leave);
      el.removeEventListener("focusin", over);
      el.removeEventListener("focusout", onFocusOut);
    };
  }, [nav, activeHref]);
}

/** Card entrance: a short stagger on the first list a visitor sees this session, then a plain fade. */
function useFirstVisit() {
  useLayoutEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem("bb-seen") === "1";
      if (!seen) sessionStorage.setItem("bb-seen", "1");
    } catch {
      seen = false;
    }
    if (!seen) document.documentElement.classList.add("bb-first");
  }, []);
}

/** Skip link target: the shell's <main>; focus it without leaving a hash in the URL. */
function skipToMain(e: MouseEvent<HTMLAnchorElement>) {
  const main = document.querySelector<HTMLElement>("main, .app-main");
  if (!main) return;
  e.preventDefault();
  if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
  main.focus();
  main.scrollIntoView({ block: "start" });
}

/* ---------------------------------- slots --------------------------------- */

/** Full lockup, or the [••] mark alone on very narrow screens (CSS picks one). Named by the SVG. */
const Logo: BrandSlots["Logo"] = ({ brand }) => (
  <>
    <LogoLockup title={brand.name} />
    <LogoMark className="bb-logo bb-logo-mark" title={brand.name} />
  </>
);

function initials(name: string | undefined, email: string | undefined): string {
  const s = (name || email || "?").trim();
  return s.slice(0, 1).toUpperCase();
}

const Header: BrandSlots["Header"] = ({ brand, nav, user, onSignIn, onSignOut }) => {
  const benchRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  useBenchEffects(benchRef, headerRef);
  useNavInk(navRef, nav.find((n) => n.active)?.href);
  useFirstVisit();
  const mineHref = nav.find((n) => n.href === "/mine")?.href;
  const who = user ? (user.name ?? user.email ?? "Account") : "";
  return (
    <>
      <div className="bb-bench" ref={benchRef} aria-hidden>
        <div className="bb-bench-clip">
          <div className="bb-bench-lens">
            <div className="bb-bench-grid" />
          </div>
        </div>
      </div>
      <a className="bb-skip" href="#main" onClick={skipToMain}>
        Skip to content
      </a>
      <header className="bb-header" ref={headerRef}>
        <a className="bb-header-brand" href="/">
          <Logo brand={brand} />
        </a>
        <nav className="bb-nav" aria-label="Primary" ref={navRef}>
          <span className="bb-nav-ink" aria-hidden />
          <span className="bb-nav-hover" aria-hidden />
          {nav.map((n) => (
            <a key={n.href} href={n.href} className={`bb-nav-link${n.active ? " is-active" : ""}`} aria-current={n.active ? "page" : undefined}>
              {n.label}
            </a>
          ))}
        </nav>
        <div className="bb-account">
          {user ? (
            <>
              {mineHref ? (
                <a className="bb-account-btn" href={mineHref} title={user.email ? `Signed in as ${user.email}` : undefined}>
                  {user.picture ? <img className="bb-avatar" src={user.picture} alt="" width={26} height={26} /> : <span className="bb-avatar" aria-hidden>{initials(user.name, user.email)}</span>}
                  <span className="bb-account-name">{who}</span>
                </a>
              ) : (
                <span className="bb-account-btn is-static" title={user.email ? `Signed in as ${user.email}` : undefined}>
                  {user.picture ? <img className="bb-avatar" src={user.picture} alt="" width={26} height={26} /> : <span className="bb-avatar" aria-hidden>{initials(user.name, user.email)}</span>}
                  <span className="bb-account-name">{who}</span>
                </span>
              )}
              <button type="button" className="bb-signout" onClick={onSignOut}>
                Sign out
              </button>
            </>
          ) : (
            <button type="button" className="bb-account-btn is-signin" onClick={onSignIn}>
              Sign in
            </button>
          )}
        </div>
      </header>
    </>
  );
};

const Footer: BrandSlots["Footer"] = ({ brand, madeWith }) => (
  <footer className="bb-footer">
    <div className="bb-footer-inner">
      <a className="bb-footer-brand" href="/">
        <LogoDomain title="BrickBrackets.io" />
      </a>
      <span className="bb-footer-note">Tolerances are stated, not guessed. If it doesn’t fit like an original piece, it doesn’t ship.</span>
      {madeWith && <span className="bb-footer-note bb-footer-local">{madeWith}</span>}
      {brand.fitTags.disclaimer && <span className="bb-footer-legal">{brand.fitTags.disclaimer}</span>}
    </div>
  </footer>
);

const Arrow = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" focusable="false" aria-hidden>
    <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CatalogCard: BrandSlots["CatalogCard"] = ({ entry, children }) => {
  const community = entry.kind === "community";
  const cta = entry.href.startsWith("/design/") ? "Configure" : "View";
  const tags = entry.tags.filter((t) => !t.startsWith("legacy:")).slice(0, 3);
  const byline = community ? [entry.designTitle, entry.author && `by ${entry.author}`].filter(Boolean).join(" · ") : "";
  const fits = community ? (entry.fits ?? []) : [];
  return (
    <a className={`bb-card${community ? " is-community" : ""}`} href={entry.href}>
      <span className="bb-card-ring" aria-hidden>
        <span className="bb-card-ring-spot" />
      </span>
      <span className="bb-card-fx" aria-hidden>
        <span className="bb-card-spot" />
      </span>
      <div className="bb-card-preview">
        {children ?? (entry.thumbnailUrl ? <img src={entry.thumbnailUrl} alt="" loading="lazy" decoding="async" /> : <span className="bb-card-placeholder">{entry.title.slice(0, 2)}</span>)}
      </div>
      <div className="bb-card-body">
        <div className="bb-card-head">
          <h3 className="bb-card-title">{entry.title}</h3>
          {!community && <span className="bb-ver">v{entry.version}</span>}
        </div>
        {community && byline ? <p className="bb-card-desc bb-card-byline">{byline}</p> : entry.description && <p className="bb-card-desc">{entry.description}</p>}
        {fits.length > 0 && (
          <ul className="bb-card-fits" aria-label={entry.fitLabel ? `Fits ${entry.fitLabel}` : "Fits"}>
            {fits.slice(0, 3).map((f) => (
              <li key={f.ref} className="bb-fit">
                <span className="bb-fit-ref">{f.ref}</span>
                {f.name && <span className="bb-fit-name">{f.name}</span>}
              </li>
            ))}
            {fits.length > 3 && <li className="bb-fit bb-fit-more">+{fits.length - 3}</li>}
          </ul>
        )}
        <div className="bb-card-foot">
          <div className="bb-card-meta">
            {community ? (
              <>
                {entry.printed && <span className="bb-signal">✓ Printed</span>}
                {!!entry.confirmCount && <span className="bb-signal-count">{entry.confirmCount} also printed</span>}
              </>
            ) : (
              tags.length > 0 && <span className="bb-tags">{tags.join(" · ")}</span>
            )}
          </div>
          <span className="bb-card-cta" aria-hidden>
            {cta}
            <Arrow />
          </span>
        </div>
      </div>
    </a>
  );
};

const EmptyState: BrandSlots["EmptyState"] = ({ title, body, action }) => (
  <div className="bb-empty">
    <LogoMark />
    <h2>{title}</h2>
    {body && <p>{body}</p>}
    {action}
  </div>
);

const ConfiguratorFrame: BrandSlots["ConfiguratorFrame"] = ({ children, aside }) => (
  <div className="bb-frame">
    <div className="bb-frame-main">{children}</div>
    {aside && <aside className="bb-frame-aside">{aside}</aside>}
  </div>
);

export const slots: Partial<BrandSlots> = { Logo, Header, Footer, CatalogCard, EmptyState, ConfiguratorFrame };
